// Lightweight Select component - replaces NextUI Select
import React, { useState, useRef, useEffect } from 'react';

const Select = ({ 
  placeholder = "Select an option",
  selectedKeys,
  onSelectionChange,
  children,
  className = '',
  variant = 'bordered',
  size = 'md',
  ...props 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(selectedKeys ? Array.from(selectedKeys)[0] : null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseClasses = "w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all duration-200 bg-white";
  
  const sizeClasses = {
    sm: "text-sm py-1.5",
    md: "text-base py-2",
    lg: "text-lg py-2.5"
  };

  const handleSelect = (key) => {
    const option = options.find(opt => opt.key === key);
    const selectedValue = option ? option.value : key;
    setSelected(key);
    setIsOpen(false);
    if (onSelectionChange) {
      // Pass the actual value, not the key
      onSelectionChange(new Set([selectedValue]));
    }
  };

  const options = React.Children.toArray(children)
    .filter(child => {
      // Check if it's a SelectItem by type name, displayName, or by checking props
      const isSelectItem = child.type?.name === 'SelectItem' || 
                          child.type?.displayName === 'SelectItem' ||
                          (child.props && ('value' in child.props || 'data-value' in child.props));
      return isSelectItem;
    })
    .map((child, index) => {
      // Extract value from props.value, props.children, or data-value attribute
      const value = child.props?.value || 
                   child.props?.children || 
                   child.props?.['data-value'] ||
                   String(child.props?.children || `option-${index}`);
      const key = child.key || child.props?.value || `option-${index}`;
      return { key, value };
    });

  const selectedOption = options.find(opt => opt.key === selected);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`${baseClasses} ${sizeClasses[size]} flex items-center justify-between cursor-pointer`}
        {...props}
      >
        <span className={selected ? "text-gray-900" : "text-gray-400"}>
          {selectedOption ? selectedOption.value : placeholder}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {options.length > 0 ? (
            options.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => handleSelect(option.key)}
                className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors ${
                  selected === option.key ? 'bg-[#DC2626] text-white' : 'text-gray-900'
                }`}
              >
                {String(option.value)}
              </button>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500 text-sm">No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

export const SelectItem = ({ value, children, ...props }) => {
  // React doesn't allow 'key' as a prop - it's handled automatically
  return <div data-value={value} {...props}>{children || value}</div>;
};

SelectItem.displayName = 'SelectItem';

export default Select;
